import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PageMeta } from "@/components/common/PageMeta";
import { ScrollSection } from "@/components/main-page/ScrollSection";
import { ShareRow } from "@/components/insights/ShareRow";
import { ArticleCard } from "@/components/insights/ArticleCard";
import { SubscribeForm } from "@/components/common/SubscribeForm";
import { useArticle, useArticles, getCategoryColor } from "@/lib/insights";

export function InsightDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { article, loading } = useArticle(slug);
  const all = useArticles();

  if (loading) {
    return <p className="px-8 py-24 text-sm text-[var(--color-fg-tertiary)]">Loading…</p>;
  }
  if (!article) {
    return <Navigate to="/insights" replace />;
  }

  const url = `https://cosx.co/insights/${article.slug}`;
  const dateFormatted = new Date(article.date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const related = all
    .filter((a) => a.slug !== article.slug)
    .slice(0, 2);

  // Thumbnails may be absolute (R2-hosted) or relative (legacy /insights/<file>).
  // Absolutise once so og:image and JSON-LD always carry a fully-qualified URL.
  const absoluteThumbnail = article.thumbnail
    ? /^https?:\/\//.test(article.thumbnail)
      ? article.thumbnail
      : `https://cosx.co${article.thumbnail}`
    : undefined;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    dateModified: article.date,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    ...(absoluteThumbnail && { image: absoluteThumbnail }),
    ...(article.category && { articleSection: article.category }),
    author: article.author
      ? { "@type": "Person", name: article.author }
      : { "@type": "Organization", name: "COSX", url: "https://cosx.co" },
    publisher: {
      "@type": "Organization",
      name: "COSX",
      url: "https://cosx.co",
      logo: { "@type": "ImageObject", url: "https://cosx.co/cosx-logo.png" },
    },
  };

  const twitterLabels = [];
  if (article.readingTime) twitterLabels.push({ label: "Reading time", data: article.readingTime });
  if (article.category) twitterLabels.push({ label: "Category", data: article.category });

  return (
    <>
      <PageMeta
        title={`${article.title} | COSX Insights`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        ogImage={absoluteThumbnail}
        ogType="article"
        article={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author || undefined,
          section: article.category || undefined,
          twitterLabels,
        }}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Cover image + back link — outside the section */}
      <div className="relative z-10 pt-[var(--topnav-height)] px-8">
        <div className="max-w-6xl mx-auto w-full pt-16">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors mb-10"
          >
            ← Back to insights
          </Link>

          {article.thumbnail && (
            <div>
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>
          )}
        </div>
      </div>

      {/* Article content — proper ScrollSection starting from label + title */}
      <ScrollSection id="article" title={article.title} compact>
        <div className="mb-4 -mt-8">
          <span className={`inline-block text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-2 py-0.5 border mb-4 ${getCategoryColor(article.category)}`}>
            {article.category}
          </span>
          <div className="flex items-center gap-4 text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
            <span>{dateFormatted}</span>
            <span>·</span>
            <span>{article.readingTime} read</span>
            {article.author && (
              <>
                <span>·</span>
                <span>By {article.author}</span>
              </>
            )}
          </div>
        </div>

        <div className="article-prose mb-12">
          <Markdown remarkPlugins={[remarkGfm]}>{article.body}</Markdown>
        </div>

        <div className="pt-6 border-t border-[var(--color-border)]">
          <ShareRow title={article.title} url={url} />
        </div>
      </ScrollSection>

      {/* More from COSX */}
      {related.length > 0 && (
        <ScrollSection id="related" title="More from COSX" compact>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {related.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </ScrollSection>
      )}

      {/* Subscribe */}
      <ScrollSection id="subscribe" title="Stay in the loop" subtitle="Get notified when we publish new insights. No spam, unsubscribe anytime." compact>
        <SubscribeForm />
      </ScrollSection>
    </>
  );
}
